import { useCallback, useEffect, useState } from 'react';
import { AsyncFilterType, QueryBuilderFilterType } from '../../filterUtils';
import { AsyncLabelLoaders, AsyncSuggestionsLoaders } from '../../types';
import {
  AsyncSelect,
  SelectOption,
  TextInputWithAutocomplete,
} from '@ardoq/select';
import {
  getAsyncLabelLoaderByAsyncFilterType,
  getAsyncOptionsLoaderByAsyncFilterType,
} from './utils';
import { IconName } from '@ardoq/icons';
import { usePrevious } from '@ardoq/hooks';
import { TextInput } from '@ardoq/forms';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { QueryBuilderText } from '../QueryBuilderTextInput';
import { QueryBuilderRuleValue } from '@ardoq/api-types';

type RuleAsyncValueSelectProps = {
  filterType: AsyncFilterType;
  value?: QueryBuilderRuleValue;
  onValueChange: (value: QueryBuilderRuleValue) => void;
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
  isQueryBuilderInReadOnlyMode: boolean;
  isFilterReadonly: boolean;
  label: string;
  inputId?: string;
  isDisabled?: boolean;
  placeholder?: string;
  hasWarning?: boolean;
  menuPortalTarget?: HTMLElement | null;
  i18nLocale?: I18nLocale;
};

const getIsInputAutocomplete = (filterType: AsyncFilterType) =>
  [
    QueryBuilderFilterType.ASYNC_COMPONENT_NAME,
    QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME,
    QueryBuilderFilterType.ASYNC_REFERENCED_COMPONENT_TYPE_NAME,
    QueryBuilderFilterType.ASYNC_REFERENCE_TYPE_NAME,
    QueryBuilderFilterType.ASYNC_WORKSPACE_NAME,
  ].includes(filterType);

// Decouple the dropdown menu width from the trigger width so option labels
// stay readable when the value column is squeezed (e.g. "Has reference" rows).
const readableMenuStyles = {
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 'max(240px, 100%)',
    width: 'auto',
  }),
};

const getIsString = (
  value: QueryBuilderRuleValue | undefined
): value is string => typeof value === 'string';

const getValueOption = (
  value: QueryBuilderRuleValue | undefined,
  isLoadingLabel: boolean,
  label: string | undefined,
  i18nLocale: I18nLocale
): SelectOption<string> | undefined => {
  if (!getIsString(value)) return undefined;
  if (isLoadingLabel) {
    return {
      iconName: IconName.SPINNER,
      value,
      label: t(
        i18nLocale,
        'queryBuilder:asyncSelect.loadingLabel',
        'Loading label...'
      ),
    };
  }
  if (value && label) {
    return { value, label };
  }
  return {
    value,
    label: t(
      i18nLocale,
      'queryBuilder:asyncSelect.unknownName',
      { value },
      'Unknown name ({{value}})'
    ),
  };
};

type UseValueOptionGetterProps = Pick<
  RuleAsyncValueSelectProps,
  'asyncLabelLoaders' | 'filterType' | 'value'
> & { isInputAutocomplete: boolean; i18nLocale: I18nLocale };

const useValueOptionGetter = ({
  asyncLabelLoaders,
  filterType,
  value,
  isInputAutocomplete,
  i18nLocale,
}: UseValueOptionGetterProps) => {
  const previousValue = usePrevious(value);
  const labelLoader = useCallback(
    (id: string) =>
      getAsyncLabelLoaderByAsyncFilterType(asyncLabelLoaders, filterType)(id),
    [asyncLabelLoaders, filterType]
  );
  const [label, setLabel] = useState<string | undefined>();
  const [isLoadingLabel, setIsLoadingLabel] = useState<boolean>(false);
  useEffect(() => {
    if (isInputAutocomplete || previousValue === value) return;
    setLabel(undefined);
    setIsLoadingLabel(false);
    (async () => {
      try {
        setIsLoadingLabel(true);
        setLabel(await labelLoader(value as string));
      } catch (error) {
        setLabel(undefined);
      }
      setIsLoadingLabel(false);
    })();
  }, [value, labelLoader, previousValue, isInputAutocomplete]);
  if (getIsString(value) && isInputAutocomplete) return { value, label: value };
  return getValueOption(value, isLoadingLabel, label, i18nLocale);
};

export const RuleAsyncValueSelect = ({
  filterType,
  value,
  onValueChange,
  asyncLabelLoaders,
  asyncSuggestionsLoaders,
  isQueryBuilderInReadOnlyMode,
  isFilterReadonly,
  label,
  inputId,
  isDisabled,
  placeholder,
  hasWarning,
  menuPortalTarget,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: RuleAsyncValueSelectProps) => {
  const resolvedPlaceholder =
    placeholder ??
    t(i18nLocale, 'queryBuilder:placeholder.typeToSearch', 'Type to search...');
  const isInputAutocomplete = getIsInputAutocomplete(filterType);
  const valueOption = useValueOptionGetter({
    asyncLabelLoaders,
    filterType,
    value,
    isInputAutocomplete,
    i18nLocale,
  });

  if (isQueryBuilderInReadOnlyMode) {
    return (
      <QueryBuilderText
        value={valueOption?.label ?? ''}
        isDisabled={isDisabled}
      />
    );
  }

  if (isFilterReadonly) {
    return (
      <TextInput
        name={label}
        value={valueOption?.label}
        isReadOnly
        dataTestId="async-readonly-input"
        inputId={inputId}
        isDisabled={isDisabled}
      />
    );
  }

  if (isInputAutocomplete) {
    // note that his approach works only for filters that have label same as value, e.g. Component type or Component name
    return (
      <TextInputWithAutocomplete
        preloadOptionsOnFocus
        placeholder={resolvedPlaceholder}
        value={value as string}
        onValueChange={onValueChange}
        loadOptions={async inputValue =>
          (
            await getAsyncOptionsLoaderByAsyncFilterType(
              asyncSuggestionsLoaders,
              filterType
            )(inputValue)
          ).map(({ label }) => label)
        }
        dataTestId="autocomplete-async-input"
        inputId={inputId}
        isDisabled={isDisabled}
        hasWarning={hasWarning}
        menuPortalTarget={menuPortalTarget}
        styles={readableMenuStyles}
      />
    );
  }
  return (
    <AsyncSelect
      defaultOptions
      placeholder={resolvedPlaceholder}
      loadOptions={async inputValue => {
        const options = await getAsyncOptionsLoaderByAsyncFilterType(
          asyncSuggestionsLoaders,
          filterType
        )(inputValue);
        return [
          {
            label: t(
              i18nLocale,
              'queryBuilder:asyncSelect.suggestions',
              'Suggestions'
            ),
            options,
          },
        ];
      }}
      value={valueOption}
      onChange={selectedOption => {
        if (!selectedOption) return;
        const { value } = selectedOption;
        onValueChange(value);
      }}
      dataTestId="async-input"
      inputId={inputId}
      isDisabled={isDisabled}
      hasWarning={hasWarning}
      menuPortalTarget={menuPortalTarget}
      styles={readableMenuStyles}
    />
  );
};
